import { Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, Subscription } from 'rxjs';
import { saveAs } from 'file-saver';
import { documentPreviewKind, DocumentPreviewKind, imageMimeType } from '../../utils/documentPreview';

@Component({
    selector: 'app-document-preview-pane',
    templateUrl: './document-preview-pane.component.html',
    styleUrls: ['./document-preview-pane.component.css']
})
export class DocumentPreviewPaneComponent implements OnChanges, OnDestroy {
    @ViewChild('docxHost') docxHost?: ElementRef<HTMLDivElement>;

    @Input() fileName = '';
    @Input() compact = false;

    loading = true;
    loadingMessage = 'Caricamento dell\'anteprima…';
    errorMessage = '';
    downloadError = '';
    downloading = false;
    kind: DocumentPreviewKind = 'unsupported';
    pdfUrl: SafeResourceUrl | null = null;
    imageUrl: string | null = null;

    private blob: Blob | null = null;
    private fileLoader: (() => Observable<Blob>) | null = null;
    private started = false;
    private destroyed = false;
    private subscription = new Subscription();
    private objectUrls: string[] = [];
    private docxAttempts = 0;

    constructor(private sanitizer: DomSanitizer,
                private zone: NgZone) {
    }

    get hasPdf(): boolean {
        return !!this.pdfUrl;
    }

    @Input()
    set loadFile(loader: (() => Observable<Blob>) | null) {
        this.fileLoader = loader;
        this.begin();
    }

    ngOnChanges(): void {
        this.begin();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.subscription.unsubscribe();
        this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    }

    download(): void {
        if (this.loading || this.downloading) {
            return;
        }
        if (this.blob) {
            saveAs(this.blob, this.fileName);
            return;
        }
        if (!this.fileLoader) {
            return;
        }
        this.downloading = true;
        this.downloadError = '';
        this.subscription.add(this.fileLoader().subscribe({
            next: (blob) => {
                this.blob = blob;
                this.downloading = false;
                saveAs(blob, this.fileName);
            },
            error: () => {
                this.downloading = false;
                this.downloadError = 'Impossibile scaricare il documento.';
            }
        }));
    }

    private begin(): void {
        if (this.started || !this.fileLoader || !this.fileName) {
            return;
        }
        this.started = true;
        this.kind = documentPreviewKind(this.fileName);
        if (this.kind === 'unsupported') {
            this.loading = false;
            this.errorMessage = 'L\'anteprima non è disponibile per questo tipo di file. Puoi scaricarlo.';
            return;
        }
        if (this.kind === 'heic') {
            this.loadingMessage = 'Conversione dell\'immagine…';
        }
        this.subscription.add(this.fileLoader().subscribe({
            next: (blob) => {
                this.blob = blob;
                this.render(blob);
            },
            error: () => this.fail('Impossibile caricare il documento.')
        }));
    }

    private render(blob: Blob): void {
        if (this.destroyed) {
            return;
        }
        if (this.kind === 'pdf') {
            const url = this.rememberUrl(URL.createObjectURL(this.typedBlob(blob, 'application/pdf')));
            this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
            this.loading = false;
            return;
        }
        if (this.kind === 'image') {
            this.imageUrl = this.rememberUrl(URL.createObjectURL(this.typedBlob(blob, imageMimeType(this.fileName))));
            this.loading = false;
            return;
        }
        if (this.kind === 'heic') {
            this.convertHeic(blob);
            return;
        }
        if (this.kind === 'docx') {
            this.loadingMessage = 'Preparazione dell\'anteprima…';
            setTimeout(() => this.renderDocx(blob));
        }
    }

    private async convertHeic(blob: Blob): Promise<void> {
        try {
            const heic2any = (await import('heic2any')).default;
            const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.92 });
            if (this.destroyed) {
                return;
            }
            const jpeg = Array.isArray(converted) ? converted[0] : converted;
            if (!jpeg) {
                this.zone.run(() => this.fail('Impossibile convertire questo file HEIC. Puoi scaricarlo.'));
                return;
            }
            const url = this.rememberUrl(URL.createObjectURL(jpeg));
            this.zone.run(() => {
                this.imageUrl = url;
                this.loading = false;
            });
        } catch {
            if (!this.destroyed) {
                this.zone.run(() => this.fail('Impossibile convertire questo file HEIC. Puoi scaricarlo.'));
            }
        }
    }

    private async renderDocx(blob: Blob): Promise<void> {
        const host = this.docxHost?.nativeElement;
        if (this.destroyed) {
            return;
        }
        if (!host) {
            if (this.docxAttempts < 8) {
                this.docxAttempts++;
                setTimeout(() => this.renderDocx(blob), 50);
                return;
            }
            this.fail('Impossibile mostrare l\'anteprima di questo file Word. Puoi scaricarlo.');
            return;
        }
        try {
            const { renderAsync } = await import('docx-preview');
            await renderAsync(blob, host, undefined, {
                inWrapper: true,
                breakPages: true,
                className: 'docx'
            });
            if (!this.destroyed) {
                this.zone.run(() => {
                    this.loading = false;
                });
            }
        } catch {
            if (!this.destroyed) {
                this.zone.run(() => this.fail('Impossibile mostrare l\'anteprima di questo file Word. Puoi scaricarlo.'));
            }
        }
    }

    private fail(message: string): void {
        this.loading = false;
        this.errorMessage = message;
        this.pdfUrl = null;
        this.imageUrl = null;
    }

    private typedBlob(blob: Blob, type: string): Blob {
        return blob.type === type ? blob : new Blob([blob], { type });
    }

    private rememberUrl(url: string): string {
        this.objectUrls.push(url);
        return url;
    }
}
